import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { navigate } from '../../services/navigationService';
import Button from '../../components/ui/Button';

import { Eye, EyeOff, CheckCircle, XCircle, Mail, Lock, User } from 'lucide-react';

// Add new imports for 2FA
import { verificationService } from '../../services/verificationService';
import { twoFactorService } from '../../services/twoFactorService';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

const getRoleDashboard = (role) => {
  switch (role) {
    case 'admin': return '/admin-dashboard';
    case 'supervisor': return '/supervisor-alert-management-dashboard';
    case 'manager': return '/ipm-operations-overview';
    default: return '/ipm-operations-overview';
  }
};

const Login = () => {
  const { signIn, signOut, user, signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'
  
  // New validation states
  const [touched, setTouched] = useState({ fullName: false, email: false, password: false, phone: false });
  const [validationErrors, setValidationErrors] = useState({ fullName: '', email: '', password: '', phone: '' });
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);

  // Get redirect info from location state
  const from = location?.state?.from || '/ipm-operations-overview';
  const redirectMessage = location?.state?.message;

  // Add logout handler
  const handleLogout = async () => {
    try {
      await signOut();
      setError('You have been logged out successfully');
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to log out. Please try again.');
    }
  };

  useEffect(() => {
    if (user) {
      // Route already-logged-in users by role
      const role = user?.user_metadata?.role || 'employee';
      const destination = from !== '/ipm-operations-overview' ? from : getRoleDashboard(role);
      navigate(destination);
    }
  }, [user, navigate, from]);

  // Real-time validation
  useEffect(() => {
    if (mode === 'signup') {
      validateInputs();
    }
  }, [fullName, email, password, phoneNumber, mode]);

  // Password strength calculator
  const calculatePasswordStrength = (pwd) => {
    let score = 0;
    if (!pwd) return { score: 0, label: '', color: '' };

    // Length check
    if (pwd?.length >= 8) score++;
    if (pwd?.length >= 12) score++;

    // Character variety
    if (/[a-z]/?.test(pwd)) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/?.test(pwd)) score++;

    // Map score to strength
    if (score <= 2) return { score: 1, label: 'Weak', color: 'bg-red-500' };
    if (score <= 4) return { score: 2, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 5) return { score: 3, label: 'Good', color: 'bg-blue-500' };
    return { score: 4, label: 'Strong', color: 'bg-green-500' };
  };

  useEffect(() => {
    if (mode === 'signup' && password) {
      setPasswordStrength(calculatePasswordStrength(password));
    }
  }, [password, mode]);

  const validateInputs = () => {
    const errors = { fullName: '', email: '', password: '', phone: '' };

    // Full name validation
    if (touched?.fullName && mode === 'signup') {
      if (!fullName?.trim()) {
        errors.fullName = 'Full name is required';
      } else if (fullName?.trim()?.length < 2) {
        errors.fullName = 'Name must be at least 2 characters';
      }
    }

    // Email validation
    if (touched?.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email) {
        errors.email = 'Email is required';
      } else if (!emailRegex?.test(email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    // Password validation
    if (touched?.password) {
      if (!password) {
        errors.password = 'Password is required';
      } else if (password?.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
    }

    // Phone validation
    if (touched?.phone && mode === 'signup') {
      const phoneRegex = /^[\d\s()+-]+$/;
      if (!phoneNumber?.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!phoneRegex?.test(phoneNumber) || phoneNumber?.replace(/\D/g, '')?.length < 10) {
        errors.phone = 'Please enter a valid phone number (at least 10 digits)';
      }
    }

    setValidationErrors(errors);
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const getInputBorderClass = (field) => {
    if (!touched?.[field]) return 'border-gray-300 focus:border-green-500';
    if (validationErrors?.[field]) return 'border-red-500 focus:border-red-500';
    return 'border-green-500 focus:border-green-500';
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Step 1: Authenticate user credentials
      const { data: authData, error: authError } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (authError) throw authError;

      // Step 2: Check if user has 2FA enabled
      const twoFactorStatus = await twoFactorService?.check2FAStatus(authData?.user?.id);

      if (twoFactorStatus?.enabled) {
        // Sign out temporarily - they'll sign in again after 2FA verification
        await supabase?.auth?.signOut();

        // Determine which verification method to use
        const method = twoFactorStatus?.method;
        
        // Get user profile for contact info
        const { data: profile } = await supabase?.from('user_profiles')?.select('email, phone')?.eq('id', authData?.user?.id)?.single();

        // Send verification code
        let verificationId;
        if (method === 'email') {
          let result = await verificationService?.sendEmailVerification(profile?.email);
          verificationId = result?.verificationId;
        } else {
          let result = await verificationService?.sendSmsVerification(profile?.phone);
          verificationId = result?.verificationId;
        }

        // Create 2FA session
        const session = await twoFactorService?.create2FASession(authData?.user?.id, verificationId);
        
        setSessionToken(session?.sessionToken);
        setTwoFactorRequired(true);
        setError('');
      } else {
        // No 2FA required - redirect based on user role
        const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', authData?.user?.id)?.single();
        const role = profile?.role || authData?.user?.user_metadata?.role || 'employee';
        navigate(getRoleDashboard(role));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error?.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    // Mark all fields as touched
    setTouched({ fullName: true, email: true, password: true, phone: true });

    // Validate all fields
    const errors = { fullName: '', email: '', password: '', phone: '' };
    
    if (!fullName?.trim()) {
      errors.fullName = 'Full name is required';
    } else if (fullName?.trim()?.length < 2) {
      errors.fullName = 'Name must be at least 2 characters';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      errors.email = 'Email is required';
    } else if (!emailRegex?.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password?.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    // Phone validation
    const phoneRegex = /^[\d\s()+-]+$/;
    if (phoneNumber?.trim() && (!phoneRegex?.test(phoneNumber) || phoneNumber?.replace(/\D/g, '')?.length < 10)) {
      errors.phone = 'Please enter a valid phone number (at least 10 digits)';
    }

    setValidationErrors(errors);

    // Check if there are any errors
    if (errors?.fullName || errors?.email || errors?.password || errors?.phone) {
      return;
    }

    // Check terms acceptance
    if (!acceptedTerms) {
      setError('Please accept the Terms and Conditions to continue');
      return;
    }

    setLoading(true);

    try {
      // Check if Supabase is configured
      if (!isSupabaseConfigured()) {
        throw new Error('Authentication service is not configured. Please contact support.');
      }

      // Call signUp from AuthContext
      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'employee',
            phone: phoneNumber,
            sms_consent: smsConsent
          }
        }
      });

      // ✅ Supabase signup diagnostic log
      console.log('[Supabase signUp] data:', data);
      console.log('[Supabase signUp] error:', signUpError);

      if (signUpError) throw signUpError;

      // Send signup confirmation email
      try {
        await supabase?.functions?.invoke('send-transactional-email', {
          body: {
            to: email,
            type: 'signup_confirmation',
            data: { fullName }
          }
        });
      } catch (emailErr) {
        console.error('Signup confirmation email failed:', emailErr);
        // Don't block signup if email fails
      }

      // Check if email confirmation is required
      if (data?.user && !data?.session) {
        // Email confirmation required - send verification code
        // Pass userId explicitly since there's no active session yet
        try {
          const verificationResult = await verificationService?.sendEmailVerification(email, data?.user?.id);
          
          // Navigate to email verification screen with email, userId, and verificationId
          navigate('/email-verification-screen', {
            state: {
              email,
              userId: data?.user?.id,
              verificationId: verificationResult?.verificationId,
              phoneNumber: phoneNumber || ''
            }
          });
        } catch (verifyErr) {
          console.error('Verification setup failed:', verifyErr);
          setSuccess('Account created! Please check your email to verify your account.');
          setMode('signin');
        }
      } else if (data?.session) {
        // Auto-confirmed - redirect to dashboard
        navigate('/ipm-operations-overview');
      }
    } catch (error) {
      console.error('Signup error:', error);
      
      // Provide specific error messages
      if (error?.message?.includes('fetch')) {
        setError('Network error: Unable to connect to authentication service. Please check your internet connection.');
      } else if (error?.message?.includes('User already registered')) {
        setError('This email is already registered. Please sign in instead.');
      } else if (error?.message?.includes('Invalid email')) {
        setError('Please enter a valid email address.');
      } else if (error?.message?.includes('Password')) {
        setError('Password must be at least 6 characters long.');
      } else if (error?.message?.includes('not configured')) {
        setError('Authentication service is not configured. Please contact support.');
      } else {
        setError(error?.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e?.preventDefault();
    setError('');
    setTwoFactorLoading(true);

    try {
      // Verify the 2FA session and code
      const result = await twoFactorService?.verify2FASession(sessionToken, twoFactorCode, useBackupCode);

      if (result?.success) {
        // Re-authenticate with original credentials
        const { data, error: signInError } = await supabase?.auth?.signInWithPassword({
          email,
          password
        });

        if (signInError) throw signInError;

        // Redirect to dashboard
        navigate('/ipm-operations-overview');
      }
    } catch (error) {
      console.error('2FA verification error:', error);
      setError(error?.message || 'Invalid verification code. Please try again.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // If user is already logged in, show logout option
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 via-emerald-500 to-teal-600">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Already Logged In</h2>
          <p className="text-gray-600 mb-6">You are currently logged in as {user?.email}</p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/ipm-operations-overview')} fullWidth>
              Go to Dashboard
            </Button>
            <Button onClick={handleLogout} variant="outline" fullWidth>
              Logout
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 2FA Verification Screen
  if (twoFactorRequired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 via-emerald-500 to-teal-600">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Two-Factor Authentication</h2>
          <p className="text-gray-600 mb-6">Enter the verification code sent to your device</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <XCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleVerify2FA} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {useBackupCode ? 'Backup Code' : 'Verification Code'}
              </label>
              <input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none"
                placeholder={useBackupCode ? 'Enter backup code' : 'Enter 6-digit code'}
                required
              />
            </div>

            <Button type="submit" loading={twoFactorLoading} fullWidth>
              Verify
            </Button>

            <button
              type="button"
              onClick={() => setUseBackupCode(!useBackupCode)}
              className="w-full text-sm text-green-600 hover:text-green-700 underline"
            >
              {useBackupCode ? 'Use verification code instead' : 'Use backup code instead'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Login/Signup Screen with Gradient Background
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Gradient Background with Branding */}
      <div className="hidden md:flex md:w-full lg:w-1/2 bg-gradient-to-br from-green-600 via-emerald-500 to-teal-600 p-6 md:p-8 lg:p-12 flex-col justify-between md:min-h-[40vh] lg:min-h-screen">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-4">GrowGuardian Analytics</h1>
          <p className="text-green-50 text-base md:text-lg">Professional IPM Management Platform</p>
        </div>
        
        {/* GrowGuardian Logo - Centered in the middle section */}
        <div className="flex justify-center items-center py-4 md:py-6 lg:py-8">
          <img
            src="/assets/images/GrowGuardianGGlogo-1763327971762.png"
            alt="GrowGuardian logo - Professional IPM Management Platform"
            className="w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 object-contain"
          />
        </div>
        
        <div className="space-y-4 md:space-y-6">
          <div className="flex items-start space-x-3 md:space-x-4">
            <div className="bg-white/20 p-2 md:p-3 rounded-lg flex-shrink-0">
              <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base md:text-lg">Real-Time Compliance Tracking</h3>
              <p className="text-green-50 text-sm md:text-base">Monitor and manage compliance across all operations</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 md:space-x-4">
            <div className="bg-white/20 p-2 md:p-3 rounded-lg flex-shrink-0">
              <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base md:text-lg">Advanced Analytics</h3>
              <p className="text-green-50 text-sm md:text-base">Data-driven insights for treatment effectiveness</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-3 md:space-x-4">
            <div className="bg-white/20 p-2 md:p-3 rounded-lg flex-shrink-0">
              <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base md:text-lg">Team Collaboration</h3>
              <p className="text-green-50 text-sm md:text-base">Seamless communication from field to office</p>
            </div>
          </div>
        </div>
        
        <div className="text-green-50 text-xs md:text-sm mt-4 md:mt-0">
          © 2026 GrowGuardian Analytics. All rights reserved.
        </div>
      </div>
      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6 md:p-8 bg-gray-50">
        <div className="max-w-md w-full">
          {/* Logo for mobile */}
          <div className="md:hidden mb-6 text-center">
            <img
              src="/assets/images/GrowGuardianGGlogo-1763327971762.png"
              alt="GrowGuardian logo"
              className="w-16 h-16 mx-auto mb-3 object-contain"
            />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
              GrowGuardian
            </h1>
            <p className="text-gray-600 mt-1 text-sm">Analytics Platform</p>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-white rounded-lg p-1 mb-6 md:mb-8 shadow-sm">
            <button
              onClick={() => setMode('signin')}
              className={`flex-1 py-2.5 md:py-2 px-4 rounded-md font-medium transition-colors text-sm md:text-base ${
                mode === 'signin' ?'bg-gradient-to-r from-green-600 to-teal-600 text-white' :'text-gray-600 hover:text-gray-800'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 md:py-2 px-4 rounded-md font-medium transition-colors text-sm md:text-base ${
                mode === 'signup' ?'bg-gradient-to-r from-green-600 to-teal-600 text-white' :'text-gray-600 hover:text-gray-800'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Welcome Message */}
          <div className="mb-5 md:mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
              {mode === 'signin' ? 'Welcome Back' : 'Create Account'}
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              {mode === 'signin' ?'Sign in to access your dashboard' :'Start your free trial with basic access'}
            </p>
          </div>

          {/* Redirect Message */}
          {redirectMessage && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-600">{redirectMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <XCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={mode === 'signin' ? handleLogin : handleSignup} className="space-y-4 md:space-y-4">
            {/* Full Name - Signup Only */}
            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e?.target?.value)}
                    onBlur={() => handleBlur('fullName')}
                    className={`w-full pl-10 pr-10 py-3 md:py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none transition-colors text-base ${
                      getInputBorderClass('fullName')
                    }`}
                    placeholder="Enter your full name"
                  />
                  {touched?.fullName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {validationErrors?.fullName ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : fullName ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : null}
                    </div>
                  )}
                </div>
                {touched?.fullName && validationErrors?.fullName && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors?.fullName}</p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  onBlur={() => handleBlur('email')}
                  className={`w-full pl-10 pr-10 py-3 md:py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none transition-colors text-base ${
                    getInputBorderClass('email')
                  }`}
                  placeholder="Enter your email"
                />
                {touched?.email && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {validationErrors?.email ? (
                      <XCircle className="w-5 h-5 text-red-500" />
                    ) : email ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : null}
                  </div>
                )}
              </div>
              {touched?.email && validationErrors?.email && (
                <p className="mt-1 text-sm text-red-600">{validationErrors?.email}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  onBlur={() => handleBlur('password')}
                  className={`w-full pl-10 pr-10 py-3 md:py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none transition-colors text-base ${
                    getInputBorderClass('password')
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-manipulation"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {touched?.password && validationErrors?.password && (
                <p className="mt-1 text-sm text-red-600">{validationErrors?.password}</p>
              )}
              
              {/* Password Strength Indicator - Signup Only */}
              {mode === 'signup' && password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Password Strength:</span>
                    <span className={`text-xs font-medium ${
                      passwordStrength?.score === 1 ? 'text-red-600' :
                      passwordStrength?.score === 2 ? 'text-yellow-600' :
                      passwordStrength?.score === 3 ? 'text-blue-600': 'text-green-600'
                    }`}>
                      {passwordStrength?.label}
                    </span>
                  </div>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4]?.map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full ${
                          level <= passwordStrength?.score
                            ? passwordStrength?.color
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Phone Number - Signup Only */}
            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e?.target?.value)}
                    onBlur={() => handleBlur('phone')}
                    className={`w-full px-4 py-3 md:py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:outline-none transition-colors text-base ${
                      getInputBorderClass('phone')
                    }`}
                    placeholder="(555) 123-4567"
                  />
                  {touched?.phone && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      {validationErrors?.phone ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : phoneNumber ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : null}
                    </div>
                  )}
                </div>
                {touched?.phone && validationErrors?.phone && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors?.phone}</p>
                )}
              </div>
            )}

            {/* Remember Me / Forgot Password - Sign In Only */}
            {mode === 'signin' && (
              <div className="flex items-center justify-between">
                <label className="flex items-center touch-manipulation">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <a href="#" className="text-sm text-green-600 hover:text-green-700 font-medium touch-manipulation">
                  Forgot Password?
                </a>
              </div>
            )}

            {/* Terms and Conditions - Signup Only */}
            {mode === 'signup' && (
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e?.target?.checked)}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1 touch-manipulation"
                />
                <label className="ml-2 text-sm text-gray-600">
                  I agree to the{' '}
                  <a href="/terms-conditions" className="text-green-600 hover:text-green-700 font-medium">
                    Terms and Conditions
                  </a>
                  {' '}and{' '}
                  <a href="/privacy-policy" className="text-green-600 hover:text-green-700 font-medium">
                    Privacy Policy
                  </a>
                </label>
              </div>
            )}

            {/* SMS Consent - Signup Only */}
            {mode === 'signup' && (
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e?.target?.checked)}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1 touch-manipulation"
                />
                <label className="ml-2 text-sm text-gray-600">
                  I agree to receive SMS messages from GROW GUARDIAN LLC at the phone number provided. Message frequency varies. Message & data rates may apply. Reply STOP to opt out or HELP for help. View our{' '}
                  <a href="/privacy-policy" className="text-green-600 hover:text-green-700 font-medium">
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a href="/terms-conditions" className="text-green-600 hover:text-green-700 font-medium">
                    Terms
                  </a>
                  .
                </label>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              loading={loading}
              fullWidth
              className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-medium py-3 md:py-2.5 touch-manipulation"
            >
              {mode === 'signin' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
                className="text-green-600 hover:text-green-700 font-medium touch-manipulation"
              >
                {mode === 'signin' ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;